import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Star, Trash2 } from "lucide-react";
import EntityFormDialog from "@/components/EntityFormDialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { canUserManageStaff } from "@/lib/staffPermissions";

const MESI_ABBR = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MESI_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function buildLast12() {
  const out = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ mese: d.getMonth() + 1, anno: d.getFullYear() });
  }
  return out;
}

export default function StarsPanel({ staff, currentUser, onCountChange }) {
  const [stars, setStars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // {mese, anno}
  const [removeTarget, setRemoveTarget] = useState(null); // star object
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const months = useMemo(() => buildLast12(), []);
  const canManage = canUserManageStaff(currentUser, staff);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/staff/${staff.id}/stars`);
      setStars(r.data || []);
      if (onCountChange) onCountChange((r.data || []).length);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [staff.id]);

  const byKey = useMemo(() => {
    const m = new Map();
    for (const s of stars) m.set(`${s.mese}-${s.anno}`, s);
    return m;
  }, [stars]);

  const now = new Date();
  const curMese = now.getMonth() + 1;
  const curAnno = now.getFullYear();
  const curAssigned = byKey.has(`${curMese}-${curAnno}`);

  const openAssign = (mese, anno) => {
    setAssignTarget({ mese, anno });
    setNote("");
    setAssignOpen(true);
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    if (!assignTarget) return;
    setSaving(true);
    try {
      await api.post(`/staff/${staff.id}/stars`, {
        mese: assignTarget.mese,
        anno: assignTarget.anno,
        note: note.trim() || null,
      });
      toast.success(`Stella assegnata per ${MESI_FULL[assignTarget.mese - 1]} ${assignTarget.anno}`);
      setAssignOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const submitRemove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/staff/${staff.id}/stars/${removeTarget.id}`);
      toast.success("Stella rimossa");
      setRemoveTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onCellClick = (mese, anno) => {
    const star = byKey.get(`${mese}-${anno}`);
    if (star) {
      const isAuthor = currentUser.role === "super_admin" || star.assegnata_da === currentUser.id;
      if (isAuthor) setRemoveTarget(star);
    } else if (canManage) {
      openAssign(mese, anno);
    }
  };

  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-5 sm:p-6" data-testid="stars-panel">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-lavender-100 dark:bg-lavender-900/30 flex items-center justify-center">
            <Star className="h-5 w-5 text-lavender-500 fill-lavender-500" />
          </div>
          <div>
            <h2 className="font-display text-lg tracking-tight">Stelle obiettivi</h2>
            <div className="text-xs text-muted-foreground">1 stella al mese per obiettivi raggiunti</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl leading-none flex items-center gap-1.5" data-testid="stars-count">
            <Star className="h-5 w-5 text-lavender-500 fill-lavender-500" />
            {stars.length}
          </div>
        </div>
      </div>

      {/* Timeline 12 mesi */}
      <div className="grid grid-cols-6 sm:grid-cols-12 gap-2" data-testid="stars-timeline">
        {months.map(({ mese, anno }) => {
          const star = byKey.get(`${mese}-${anno}`);
          const isCurrent = mese === curMese && anno === curAnno;
          const filled = !!star;
          const interactive =
            (filled && (currentUser.role === "super_admin" || star.assegnata_da === currentUser.id)) ||
            (!filled && canManage);
          return (
            <button
              type="button"
              key={`${mese}-${anno}`}
              onClick={() => interactive && onCellClick(mese, anno)}
              disabled={!interactive}
              title={filled
                ? `Assegnata da ${star.assegnata_da_nome} il ${new Date(star.data_assegnazione).toLocaleDateString("it-IT")}${star.note ? ` · ${star.note}` : ""}`
                : `Nessuna stella per ${MESI_FULL[mese - 1]} ${anno}`}
              className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg border transition-all
                ${filled ? "bg-lavender-50 dark:bg-lavender-900/20 border-lavender-300/60 dark:border-lavender-700/60" : "bg-background/40 border-border/60"}
                ${interactive ? "hover:scale-105 hover:border-lavender-400 cursor-pointer" : "cursor-default opacity-90"}
                ${isCurrent ? "ring-2 ring-lavender-400/50" : ""}`}
              style={{ minHeight: 64 }}
              data-testid={`star-cell-${mese}-${anno}`}
            >
              <Star
                className={`h-5 w-5 ${filled ? "text-lavender-500 fill-lavender-500" : "text-slate-300 dark:text-slate-600"}`}
              />
              <div className="text-[10px] font-medium mt-1">{MESI_ABBR[mese - 1]}</div>
              <div className="text-[9px] text-muted-foreground">{String(anno).slice(-2)}</div>
            </button>
          );
        })}
      </div>

      {/* Action bar */}
      {canManage && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => openAssign(curMese, curAnno)}
            disabled={curAssigned}
            data-testid="assign-star-current"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
            <Star className="h-4 w-4" />
            {curAssigned ? `Stella già assegnata per ${MESI_FULL[curMese - 1]}` : `Assegna stella per ${MESI_FULL[curMese - 1]}`}
          </button>
        </div>
      )}

      {/* Lista completa */}
      {stars.length > 0 && (
        <details className="mt-4 group">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">
            Vedi tutte le {stars.length} stelle
          </summary>
          <ul className="mt-3 divide-y divide-border/60" data-testid="stars-list">
            {stars.map((s) => {
              const canRemove = currentUser.role === "super_admin" || s.assegnata_da === currentUser.id;
              return (
                <li key={s.id} className="py-2.5 flex items-center gap-3" data-testid={`star-item-${s.id}`}>
                  <Star className="h-4 w-4 text-lavender-500 fill-lavender-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{MESI_FULL[s.mese - 1]} {s.anno}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      Assegnata da {s.assegnata_da_nome} · {new Date(s.data_assegnazione).toLocaleDateString("it-IT")}
                      {s.note ? ` · ${s.note}` : ""}
                    </div>
                  </div>
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => setRemoveTarget(s)}
                      className="h-8 w-8 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center justify-center text-muted-foreground hover:text-rose-500"
                      data-testid={`remove-star-${s.id}`}
                      aria-label="Rimuovi stella"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}

      {loading && stars.length === 0 && (
        <div className="text-xs text-muted-foreground mt-3">Caricamento stelle…</div>
      )}

      {/* Assign modal */}
      <EntityFormDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        title="Assegna stella obiettivi"
        description={`Riconoscimento mensile per obiettivi raggiunti da ${staff.nome} ${staff.cognome}.`}
        size="md"
        onSubmit={submitAssign}
        submitLabel="Assegna stella"
        saving={saving}
        testId="star-assign-form"
        submitTestId="star-assign-submit"
      >
        <div className="space-y-2">
          <Label>Mese / Anno</Label>
          <div className="px-3 py-2 rounded-lg border border-border/60 bg-background/40 text-sm font-medium">
            {assignTarget ? `${MESI_FULL[assignTarget.mese - 1]} ${assignTarget.anno}` : "—"}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Note <span className="text-muted-foreground text-xs">(opzionale, max 500)</span></Label>
          <Textarea
            data-testid="star-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            placeholder="Es. ha gestito ottimamente il check-in nel periodo di alta stagione…"
            rows={3}
          />
        </div>
      </EntityFormDialog>

      {/* Remove confirm */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent className="ih-glass-strong">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere la stella?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget && (
                <>Stai per rimuovere la stella di <strong>{MESI_FULL[removeTarget.mese - 1]} {removeTarget.anno}</strong> per {staff.nome} {staff.cognome}. L'azione è irreversibile.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={submitRemove} data-testid="star-remove-confirm" className="bg-rose-500 hover:bg-rose-600 text-white">
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
